/**
 * One game, opened up.
 *
 * Shared by the Games list and by the records boards, because they arrive at the same question
 * from opposite ends. The list is browsing — "what happened in week 4" — and the leaderboard is
 * an argument: somebody has just been told the narrowest win in league history was 0.12 points
 * and does not believe it. Both want the same answer, which is the two lineups, so both get the
 * same component rather than two that drift apart.
 *
 * Where a box score has not been collected the row says so in words. An empty table would read as
 * a game where nobody scored, which is a worse lie than admitting the gap.
 */

import { useMemo } from 'react';
import {
  benchPoints, blanksAreZero, internedNames, linePoints, linePosition, splitBoxName, boxFor,
  type BoxSide,
} from '../domain/boxScores.js';
import { clubFor } from '../domain/boxClubs.js';
import { NflChip } from './NflChip.js';
import { managerShort } from '../domain/identity.js';
import type { GameRow } from '../domain/records.js';

export function GameDetail({ g, boxes }: { g: GameRow; boxes: number }) {
  const box = useMemo(
    () => boxFor(g.season, g.week, g.home.team),
    [g.season, g.week, g.home.team, boxes],
  );

  return (
    <div className="boxscore">
      {box ? (
        <div className="boxsides">
          <BoxColumn side={box.mine} season={g.season}
            label={g.home.gm ? managerShort(g.home.gm) : g.home.team} />
          <BoxColumn side={box.theirs} season={g.season}
            label={g.away.gm ? managerShort(g.away.gm) : g.away.team} />
          {!box.totalsAgree && (
            <p className="chip warning block boxwarn">
              The starters do not add up to the total Yahoo printed for this game, so one of the
              lines below is being read wrongly. The team score above is the one to trust.
            </p>
          )}
        </div>
      ) : (
        <p className="note">
          <b>No box score collected for this game yet.</b> Yahoo keeps the per-player lines on a
          separate page for each matchup, gathered one at a time from a signed-in browser. Team
          totals, which every board here is built on, are complete.
        </p>
      )}
      <dl className="gamefacts">
        <div><dt>Combined</dt><dd>{g.total.toFixed(2)}</dd></div>
        <div><dt>Margin</dt><dd>{g.margin.toFixed(2)}</dd></div>
        <div>
          <dt>Result</dt>
          <dd>
            {g.winner === 'tie' ? 'Tie' : (
              <>
                {managerShort((g.winner === 'home' ? g.home.gm : g.away.gm) ?? '')
                  || (g.winner === 'home' ? g.home.team : g.away.team)} won
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Week</dt>
          <dd>
            {g.kind === 'regular' ? `Regular season, week ${g.week}` : `${g.kind}, week ${g.week}`}
          </dd>
        </div>
        {box && [box.mine, box.theirs].map((s, i) => {
          const b = benchPoints(s);
          return (
            <div key={`${s.team}${i}`}>
              <dt>{i === 0 ? 'Home' : 'Away'} bench</dt>
              <dd>{b.bench.toFixed(2)} <span className="mut">left out</span></dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

/** One side of a box score: the starters that counted, then the bench that did not. */
function BoxColumn({ side, label, season }: { side: BoxSide; label: string; season: number }) {
  const b = benchPoints(side);
  /*
   * Does a blank on this side mean nothing scored, or nothing recorded? The starters that DO carry
   * a number are checked against the total Yahoo printed; where they reconcile, every blank here
   * is a zero and is drawn as one. See `blanksAreZero`.
   */
  const zeroed = blanksAreZero(side);
  const line = (l: BoxSide['st'][number], i: number) => {
    // Yahoo glued the player's name to a "Video Forecast" link, a notes icon and that week's NFL
    // result. The result survives on the hint; the furniture does not survive at all.
    const { name, game } = splitBoxName(l[0]);
    const pts = linePoints(l, zeroed);
    /*
     * The slot is not the position for a bench or flex row, so the chip is resolved from every
     * other appearance the same player makes in the archive. The SLOT keeps its own column — a
     * reader needs to know a man was benched as well as what he plays.
     */
    const pos = linePosition(l);
    /*
     * THE CLUB HE PLAYED FOR THAT SEASON, not the one he plays for now.
     *
     * The archive has no club in it — see `domain/boxClubs.ts` — so this comes from a parallel
     * file built per season. Which is the whole point: reading it off the current player index
     * would print LV beside a 2009 Raider and LAR beside a 2010 St. Louis Ram. Where it cannot
     * be resolved (two Alex Smiths on rosters at once) there is no chip rather than a guess.
     */
    const club = clubFor(season, l[0], internedNames());
    return (
      <li key={`${l[0]}${i}`}>
        <span className="pos">{l[1]}</span>
        <span className="pl" title={game ? `${name} — ${game}` : name}>{name}</span>
        {club && <NflChip team={club} title={`${club} in ${season}`} />}
        {pos && <span className={`poschip ident pos-${pos}`}>{pos}</span>}
        <span className={`pts ${pts === null ? 'nopts' : ''}`}
          title={pts === null ? 'Not recorded — this side\u2019s starters do not add up to the printed total' : undefined}>
          {pts === null ? '—' : pts.toFixed(2)}
        </span>
      </li>
    );
  };
  return (
    <div className="boxcol">
      <div className="boxhead">
        <b>{label}</b>
        <span className="mut">{side.team}</span>
        <span className="tot">{side.pts === null ? '—' : side.pts.toFixed(2)}</span>
      </div>
      <ul className="boxlines">{side.st.map(line)}</ul>
      {side.bn.length > 0 && (
        <>
          <div className="benchhead">
            <span className="mut">Bench</span>
            <span className="tot mut">{b.bench.toFixed(2)}</span>
          </div>
          <ul className="boxlines bench">{side.bn.map(line)}</ul>
        </>
      )}
    </div>
  );
}
